import { SKYROVER_COLORS } from '@/theme/skyroverTheme';
import { useToolboxStore } from '@/state/toolboxStore';
import { ADDON_CATEGORY_CATALOG, type AddonCategoryDefinition } from '@skyrover/blockly-toolbox';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';

interface AddBlocksDialogProps {
  open: boolean;
  onClose: () => void;
}

const DIALOG_MAX_WIDTH_PX = 720;

function AddonCategoryCard({
  addon,
  added,
  onToggle,
}: {
  addon: AddonCategoryDefinition;
  added: boolean;
  onToggle: () => void;
}) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onToggle}
      aria-pressed={added}
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1,
        p: 1.5,
        border: '2px solid',
        borderColor: added ? 'success.main' : '#E0E0E0',
        borderRadius: 2,
        bgcolor: '#fff',
        cursor: 'pointer',
        textAlign: 'center',
        transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
        boxShadow: added ? '0 0 0 1px rgba(46, 125, 50, 0.35)' : 'none',
        '&:hover': {
          borderColor: added ? 'success.main' : '#BDBDBD',
        },
      }}
    >
      {added ? (
        <CheckCircleIcon
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            color: 'success.main',
            fontSize: 18,
          }}
        />
      ) : null}
      <Box
        className={`skyrover-toolbox-icon skyrover-toolbox-icon--${addon.iconId}`}
        sx={{ flexShrink: 0 }}
        aria-hidden
      />
      <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.8rem' }}>
        {addon.name}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem', lineHeight: 1.3 }}>
        {addon.description}
      </Typography>
    </Box>
  );
}

export function AddBlocksDialog({ open, onClose }: AddBlocksDialogProps) {
  const enabledAddonIds = useToolboxStore((s) => s.enabledAddonIds);
  const addAddonCategory = useToolboxStore((s) => s.addAddonCategory);
  const removeAddonCategory = useToolboxStore((s) => s.removeAddonCategory);

  const toggleAddonCategory = (id: string): void => {
    if (enabledAddonIds.includes(id)) {
      removeAddonCategory(id);
      return;
    }
    addAddonCategory(id);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: `min(${DIALOG_MAX_WIDTH_PX}px, 80vw)`,
          maxWidth: '80%',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          pr: 5,
          py: 1.5,
          fontSize: '1rem',
        }}
      >
        Add blocks
        <IconButton
          aria-label="Close"
          onClick={onClose}
          size="small"
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ bgcolor: '#FAFAFA', px: 2, py: 1.5 }}>
        <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', mb: 1.5 }}>
          Click a category to add it to the toolbox. Click again to remove it.
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)' },
            gap: 1.5,
          }}
        >
          {ADDON_CATEGORY_CATALOG.map((addon) => (
            <AddonCategoryCard
              key={addon.id}
              addon={addon}
              added={enabledAddonIds.includes(addon.id)}
              onToggle={() => toggleAddonCategory(addon.id)}
            />
          ))}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.5 }}>
        <Button onClick={onClose} size="small">
          Close
        </Button>
        <Button
          onClick={onClose}
          variant="contained"
          size="small"
          sx={{
            bgcolor: SKYROVER_COLORS.header,
            '&:hover': { bgcolor: SKYROVER_COLORS.headerHover },
          }}
        >
          OK
        </Button>
      </DialogActions>
    </Dialog>
  );
}
